import { useEffect, useState } from "react";
import {
  useAppDesignPdf,
  useAssignedClientSelection,
  useBusyAction,
  useFetch,
  useRequest,
} from "../../../hooks";
import { DownloadAction } from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { WellProps, DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import { dateInRange, displayDate, fmtDate } from "../../../utils/docDate";

interface WellsFetchResponse {
  success?: boolean;
  data: WellProps[];
}

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByProposalId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const pid = row.proposalId;
    if (!pid) return;
    const cur = map.get(pid);
    if (!cur || new Date(row.uploadedAt ?? row.date).getTime() >
      new Date(cur.uploadedAt ?? cur.date).getTime()) {
      map.set(pid, row);
    }
  });
  return map;
};

export const ProposalClient = () => {
  const { handleRequest } = useRequest();
  const { downloadProposalPdf } = useAppDesignPdf();
  const {
    clientOptions,
    selectedClientId,
    setSelectedClientId,
    showClientFilter,
  } = useAssignedClientSelection();
  const { paginationModel, setPaginationModel } = usePaginator("proposal-client", 8);

  const { data: wellsResp, isLoading: wellsLoading } =
    useFetch<WellsFetchResponse>(
      selectedClientId ? `well/client/${selectedClientId}` : undefined,
    );
  const wells: WellProps[] = wellsResp?.data ?? [];

  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [source, setSource] = useState(""); // "" = all
  const [owner, setOwner] = useState(""); // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const getWellName = (idWell?: number) =>
    wells.find((w) => w.id === idWell)?.name ?? "";

  useEffect(() => {
    if (!selectedClientId) return;
    setSelectedWellId(null);
    setRowsLoading(true);
    const opts: RequestInit = { method: "GET" };
    Promise.all([
      handleRequest({ endpoint: `proposal-list/client/${selectedClientId}`, options: opts }),
      handleRequest({ endpoint: `proposal-detail?client=${selectedClientId}`, options: opts }),
    ])
      .then(([uploadedRes, appRes]) => {
        const appRows = getRows(appRes);
        const appById = new Map<number, any>(
          appRows.map((item: any) => [item.id, item]),
        );
        const uploaded: DocRow[] = (
          isSuccessful(uploadedRes) ? uploadedRes.data : []
        ).map((item: any) => {
          const linkedProposal = appById.get(item.proposalId);
          return {
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: displayDate(item),
            uploadedAt: item.date ?? "",
            wellId: item.idWell,
            wellName: "",
            source: "uploaded" as const,
            path: item.path,
            owner: linkedProposal?.createdBy ?? "—",
            proposalId: item.proposalId,
          };
        });
        const uploadsByProposal = latestUploadByProposalId(uploaded);
        const app: DocRow[] = appRows.map((item: any) => {
          const def = uploadsByProposal.get(item.id);
          return {
            rowId: `a-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: item.createdBy ?? "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        }).filter((row: DocRow) => row.appId == null || !uploadsByProposal.has(row.appId));
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!wells.length || !rows.length) return;
    setRows((prev) =>
      prev.map((r) => (r.wellName ? r : { ...r, wellName: getWellName(r.wellId) })),
    );
  }, [wells]);

  const downloadUploaded = (path: string, name: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const opts: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key: path }),
        headers: { responseType: "Buffer" } as any,
      };
      handleRequest({ endpoint: "pdf-proposal-adm", options: opts })
        .then((res) => {
          if (!res.success) { reject(new Error("Failed")); return; }
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve();
        })
        .catch(reject);
    });

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      await downloadUploaded(row.path, row.customName);
    } else if (row.source === "app" && row.defaultUploadPath) {
      await downloadUploaded(row.defaultUploadPath, row.defaultUploadName ?? row.customName);
    } else if (row.source === "app" && row.appId != null) {
      await downloadProposalPdf(row.appId, row.customName);
    }
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(handleDownload);

  // ---- Derived: well filter -> search -> source/owner/date filters ----
  const filteredByWell =
    selectedWellId != null ? rows.filter((r) => r.wellId === selectedWellId) : rows;

  let filtered = docSearch.trim()
    ? filteredByWell.filter((r) =>
        r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()),
      )
    : filteredByWell;
  if (source) filtered = filtered.filter((r) => r.source === source);
  if (owner) filtered = filtered.filter((r) => r.owner === owner);
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      return dateInRange(r.date, dateFrom, dateTo);
    });
  }

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Proposal", render: (r) => r.customName, width: "2fr" },
    { key: "wellName", header: "Well", render: (r) => r.wellName || "—", width: "160px" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
    {
      key: "source",
      header: "Source",
      render: (r) => (r.source === "app" ? "App Design" : "Uploaded"),
      width: "110px",
    },
    { key: "owner", header: "Created by", render: (r) => r.owner, width: "160px" },
  ];

  const filters: ListFilterField[] = [
    {
      kind: "select",
      key: "source",
      label: "Source",
      value: source,
      onChange: setSource,
      options: [
        { value: "app", label: "App Design" },
        { value: "uploaded", label: "Uploaded" },
      ],
    },
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];
  if (ownerOptions.length > 0) {
    filters.splice(1, 0, {
      kind: "select",
      key: "owner",
      label: "Created by",
      value: owner,
      onChange: setOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    });
  }

  return (
    <ListPageLayout<DocRow>
      title="Proposals"
      subtitle="Browse, filter and download client proposals"
      columns={columns}
      rows={pageRows}
      getRowId={(r) => r.rowId}
      rowsLoading={rowsLoading}
      isError={isError}
      searchValue={docSearch}
      onSearchChange={setDocSearch}
      searchPlaceholder="Search proposals…"
      filters={filters}
      page={currentPage}
      pageSize={paginationModel.pageSize}
      totalCount={totalCount}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      rowsPerPageOptions={[8, 15, 25, 50]}
      sidePanel={{
        title: "Wells",
        items: wells.map((w) => ({ id: w.id ?? -1, label: w.name })),
        selectedId: selectedWellId,
        onSelect: (id) => {
          setSelectedWellId(id as number | null);
          setPaginationModel({ ...paginationModel, page: 0 });
        },
        loading: wellsLoading,
        searchPlaceholder: "Search wells…",
      }}
      toolbarActions={
        showClientFilter && clientOptions?.length ? (
          <select
            value={selectedClientId ? String(selectedClientId) : ""}
            onChange={(e) => {
              const c =
                clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
              setSelectedClientId(c?.id ?? null);
            }}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        ) : undefined
      }
      rowActions={(r) => (
        <DownloadAction
          rowId={r.rowId}
          busy={isDownloading}
          title="Download PDF"
          onClick={() => runDownload(r)}
        />
      )}
    />
  );
};
